import React, { useState } from 'react';
import Sidebar from '../components/Sidebar';
import Topbar from '../components/Topbar';
import SonarPanel from '../components/SonarPanel';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Radar, Waves, Activity } from 'lucide-react';

const SonarPage = () => {
  const [layers, setLayers] = useState({
    depth: true,
    temperature: true,
    salinity: false,
  });

  const [vesselData] = useState({
    lat: -23.745,
    lon: -134.120,
    speed: 9.8,
    heading: 212,
  });

  const toggleLayer = (layer) => {
    setLayers(prev => ({
      ...prev,
      [layer]: !prev[layer],
    }));
  };

  return (
    <div 
      className="grid grid-cols-1 lg:grid-cols-[auto_1fr] lg:grid-rows-[56px_1fr] min-h-screen bg-background text-foreground"
      data-testid="sonar-page-container"
    >
      <Sidebar layers={layers} toggleLayer={toggleLayer} currentRoute="/sonar" />
      <Topbar vesselData={vesselData} />
      <main className="pt-14 lg:pt-0 lg:col-start-2 lg:row-start-2 p-4 md:p-6 relative z-0">
        <div className="mb-6" data-testid="page-header">
          <div className="flex items-center gap-3 mb-2">
            <Radar className="h-6 w-6 text-primary" />
            <h1 className="text-2xl font-bold">Multibeam Sonar</h1>
          </div>
          <p className="text-muted-foreground">Real-time seafloor mapping and acoustic data visualization</p>
        </div>

        <div className="grid grid-cols-12 gap-4 md:gap-6" data-testid="sonar-page-grid">
          <div className="col-span-12 xl:col-span-8">
            <SonarPanel />
          </div>

          <Card className="col-span-12 xl:col-span-4" data-testid="sonar-settings-card">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Activity className="h-4 w-4 text-primary" />
                <span className="text-sm font-medium tracking-wide text-muted-foreground uppercase">Sonar Settings</span>
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <div>
                <h4 className="text-sm font-medium mb-3">System Configuration</h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Frequency</span>
                    <span className="font-mono">12 kHz</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Beam Angle</span>
                    <span className="font-mono">120°</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Range</span>
                    <span className="font-mono">5000 m</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Resolution</span>
                    <span className="font-mono">0.5 m</span>
                  </div>
                </div>
              </div>

              <div className="pt-4 border-t border-border/40">
                <h4 className="text-sm font-medium mb-3 flex items-center gap-2">
                  <Waves className="h-4 w-4" />
                  Performance Metrics
                </h4>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Coverage</span>
                    <span className="font-mono text-emerald-400">98.2%</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Signal Quality</span>
                    <span className="font-mono text-emerald-400">Excellent</span>
                  </div>
                  <div className="flex justify-between py-2 border-b border-border/30">
                    <span className="text-muted-foreground">Data Rate</span>
                    <span className="font-mono">2.4 MB/s</span>
                  </div>
                  <div className="flex justify-between py-2">
                    <span className="text-muted-foreground">System Status</span>
                    <span className="flex items-center gap-1.5">
                      <span className="h-1.5 w-1.5 rounded-full bg-emerald-400 animate-pulse"></span>
                      <span className="text-emerald-400 font-mono">Active</span>
                    </span>
                  </div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </main>
    </div>
  );
};

export default SonarPage;
